import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import PackageStepIndicator from '@/components/PackageStepIndicator';
import SignatureCanvas from 'react-signature-canvas';
import { getPackage, getStepPath, getLevelStyles } from '@/data/packageData';
import { usePackage } from '@/context/PackageContext';

const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';
const COMPANY_STAMP_URL = '/images/Xero_Stamp.png';
const DIRECTOR_SIGNATURE_URL = '/images/Director_Signature.png';

const POPIConsent = () => {
  const { packageSlug } = useParams();
  const navigate = useNavigate();
  const pkg = getPackage(packageSlug);
  const { clientData, updateClientData } = usePackage();
  const data = clientData || {};

  const [signedAt, setSignedAt] = useState(data?.signedAtPOPI || '');
  const [signature, setSignature] = useState(data?.signaturePOPI || null);
  const [locationError, setLocationError] = useState('');
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const signatureRef = useRef(null);

  useEffect(() => {
    if (!pkg) navigate('/', { replace: true });
    window.scrollTo(0, 0);
  }, [pkg, navigate]);

  // Restore previously-saved signature into the canvas (if returning to this page)
  useEffect(() => {
    if (signature && signatureRef.current && typeof signatureRef.current.fromDataURL === 'function') {
      try { signatureRef.current.fromDataURL(signature); } catch (_) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-persist any changes to location / signature so values survive back-navigation
  useEffect(() => {
    updateClientData({ ...data, signedAtPOPI: signedAt, signaturePOPI: signature });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedAt, signature]);

  if (!pkg) return null;

  // Today's date pieces
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthName = today.toLocaleDateString('en-ZA', { month: 'long' });
  const fullYear = today.getFullYear();
  const todayFormatted = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  // Pull the client identity captured on Personal Details page
  const fullName = [data.firstName, data.secondName, data.middleName, data.surname]
    .filter(Boolean)
    .join(' ') || '[Client Full Name]';
  const companyName = data.companyName || '[Company Name]';

  const validateLocation = (value) => {
    if (!value || value.trim() === '') return '';
    const locationRegex = /^[a-zA-Z\s]+$/;
    if (!locationRegex.test(value)) return 'Location can only contain letters (no numbers or special characters)';
    return '';
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSignedAt(value);
    setLocationError(validateLocation(value));
  };

  const handleEndStroke = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setSignature(signatureRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setSignature(null);
  };

  const isFormValid = () => signedAt && signedAt.trim() !== '' && !!signature && !locationError;

  const proceedToNextStep = () => {
    updateClientData({ ...data, signedAtPOPI: signedAt.trim(), signaturePOPI: signature });
    navigate(getStepPath(packageSlug, 'pricing'));
  };

  const handleSubmit = () => {
    if (!signedAt || signedAt.trim() === '') {
      setErrorModal({ open: true, message: 'Please enter the location where you are signing.' });
      return;
    }
    const err = validateLocation(signedAt);
    if (err) {
      setLocationError(err);
      setErrorModal({ open: true, message: err });
      return;
    }
    if (!signature || (signatureRef.current && signatureRef.current.isEmpty())) {
      setErrorModal({ open: true, message: 'Please provide your signature before proceeding.' });
      return;
    }
    // Check the location consistency vs the SARS Power of Attorney page
    const poaLocation = (data.signedAtPOA || '').trim();
    const popiLocation = signedAt.trim();
    if (poaLocation && popiLocation && poaLocation.toLowerCase() !== popiLocation.toLowerCase()) {
      setShowLocationWarning(true);
      return;
    }
    proceedToNextStep();
  };

  const levelStyles = getLevelStyles(pkg.colorKey);

  // Reusable inline blue-highlight pill
  const InlinePill = ({ children, testid }) => (
    <span
      data-testid={testid}
      className="inline-block bg-blue-50 text-tca-blue font-bold rounded px-2 py-0.5 mx-0.5 border-b-2 border-tca-blue"
    >
      {children}
    </span>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Logo */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img src={LOGO_URL} alt="TCA Auditors Logo" className="h-24 sm:h-32 w-auto object-contain select-none" draggable="false" />
          <img src={BANNER_URL} alt="Tax & Auditing Consultants - For Businesses & Individuals" className="h-[4.5rem] sm:h-24 w-auto object-contain" />
        </div>

        {/* Heading */}
        <div className="mb-4 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Top back button */}
        <div className="w-full max-w-3xl flex justify-start mb-6">
          <Button
            onClick={() => navigate(getStepPath(packageSlug, 'poa'))}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-poa-top"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SARS Power of Attorney
          </Button>
        </div>

        <Card className="w-full max-w-3xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10 space-y-6">
            {/* Package badge */}
            <div className="flex justify-center">
              <span className="text-sm font-bold px-4 py-1.5 rounded-full" style={levelStyles} data-testid="package-badge">
                {pkg.fullName}
              </span>
            </div>

            {/* Page heading */}
            <h2 className="text-2xl sm:text-3xl font-extrabold text-tca-blue text-center underline decoration-2 underline-offset-4">
              POPI Consent Agreement
            </h2>

            {/* Parties Section */}
            <div className="space-y-3 text-sm sm:text-base text-foreground leading-relaxed pt-2">
              <p>
                <InlinePill testid="popi-companyname">{companyName}</InlinePill>
              </p>
              <p className="text-center font-bold">And</p>
              <p>
                <InlinePill testid="popi-fullname">{fullName}</InlinePill>{' '}
                <span className="italic">(jointly referred to as <strong>"The Client"</strong>)</span>
              </p>
              <p className="text-center font-bold">And</p>
              <p>
                <strong>TC Auditors (Pty) Ltd</strong> (<strong>"the Company"</strong>)
              </p>
              <p className="pt-2">
                On <InlinePill testid="popi-date">{todayFormatted}</InlinePill> (<strong>"Date of Agreement"</strong>)
              </p>
            </div>

            <div className="border-t border-border" />

            {/* 1. Preamble */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">1. Preamble</h3>
              <p className="popi-clause">
                <span className="clause-num">1.1</span>
                <span className="clause-body">
                  the Client and Company hereby conclude an agreement relating to a POPI Consent Agreement
                  (<strong>"The Agreement"</strong>) and this said agreement contains the following provisions:
                </span>
              </p>
            </section>

            {/* 2. Introduction */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">2. Introduction</h3>
              <p className="popi-clause">
                <span className="clause-num">2.1</span>
                <span className="clause-body">
                  This agreement will be applicable to all personal information as defined in the Protection
                  of Personal Information Act, 4 of 2013 (<strong>"POPI"</strong>). By either Party submitting any
                  personal information to the other, the disclosing Party unconditionally and voluntarily, consents
                  to the processing of the submitted personal information for any and all purposes related to this
                  agreement.
                </span>
              </p>
            </section>

            {/* 3. Recitals */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">3. Recitals</h3>
              <p className="popi-clause">
                <span className="clause-num">3.1</span>
                <span className="clause-body">
                  The Parties agrees and consent that its personal information may be processed by, or on
                  behalf of either of the Parties for the purposes set out in the Agreement.
                </span>
              </p>
              <p className="popi-clause">
                <span className="clause-num">3.2</span>
                <span className="clause-body">
                  The Parties shall at all times comply with its obligations and procure that each of its
                  Affiliates comply with their obligations under POPI.
                </span>
              </p>
              <p className="popi-clause">
                <span className="clause-num">3.3</span>
                <span className="clause-body">
                  The Parties shall ensure that any personal information that is processed by it in the course
                  of performing its obligations under the Agreement is done in accordance with POPI.
                </span>
              </p>
            </section>

            {/* 4. Provisions */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">4. Provisions</h3>
              <p className="popi-clause">
                <span className="clause-num">4.1</span>
                <span className="clause-body">Each Party shall not process, disclose, or use personal information except:</span>
              </p>
              <ul className="list-disc pl-14 space-y-1">
                <li>to the extent necessary for the provision of Services and/or Products under the Agreement; or</li>
                <li>to fulfil their own obligations under the Agreement; or</li>
                <li>as otherwise expressly authorised by the other Party in writing.</li>
              </ul>
              <p className="popi-clause">
                <span className="clause-num">4.2</span>
                <span className="clause-body">
                  Each Party shall not disclose any personal information to any Third Party without the other
                  Party's prior written consent in each instance, other than to the extent required by any Regulator
                  or Law.
                </span>
              </p>
              <p className="popi-clause">
                <span className="clause-num">4.3</span>
                <span className="clause-body">
                  In the event the other Party providing such consent necessary for the disclosure of personal
                  information to a Third Party, each Party shall:
                </span>
              </p>
              <ul className="list-disc pl-14 space-y-1">
                <li>make such disclosure in compliance with POPI; and</li>
                <li>
                  enter into a written agreement with the applicable Third-Party recipient of such personal
                  information that requires such Third Party to safeguard the personal information in a manner
                  no less restrictive than each Party's obligations under these terms.
                </li>
              </ul>
              <p className="popi-clause">
                <span className="clause-num">4.4</span>
                <span className="clause-body">
                  The Parties shall implement and maintain an effective security safeguards that includes, but
                  is not limited to administrative, technical, and physical safeguards, and appropriate technical
                  and organisational measures, in each case, adequate to insure the security and confidentiality
                  of personal information, and to protect against any anticipated risks to the security or
                  integrity of personal information, protect against unauthorized access to or use of personal
                  information, protect personal information against unlawful processing or processing otherwise
                  than in accordance with this agreement, and protect against accidental loss, destruction, damage,
                  alteration or disclosure of personal information.
                </span>
              </p>
              <p className="popi-clause">
                <span className="clause-num">4.5</span>
                <span className="clause-body">
                  Without limiting the foregoing, such safeguards and measures shall be appropriate to protect
                  against the harm that may result from unauthorised or unlawful processing, use or disclosure, or
                  accidental loss, destruction, or damage to or of Personal Information and the nature of the
                  personal information, and shall maintain all safeguard measures as is required by POPI.
                </span>
              </p>
            </section>

            {/* 5. Cross Border */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">5. Cross Border</h3>
              <p className="popi-clause">
                <span className="clause-num">5.1</span>
                <span className="clause-body">
                  Each Party shall not use, process, store, transfer or permit access to any personal
                  information across the borders of South Africa, without the written consent of the other Party.
                </span>
              </p>
            </section>

            {/* 6. Breach */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">6. Breach</h3>
              <p className="popi-clause">
                <span className="clause-num">6.1</span>
                <span className="clause-body">
                  In the event of any actual, suspected, or alleged security breach, including, but not limited
                  to, loss, damage, destruction, theft, unauthorized use, access to or disclosure of any personal
                  information, each Party shall:
                </span>
              </p>
              <ul className="list-disc pl-14 space-y-1">
                <li>notify the other Party as soon as practicable after becoming aware of such event.</li>
                <li>
                  provide the other Party will all information regarding the breach in the Party's knowledge and
                  possession to allow the Party to ascertain what has occurred and which personal information has
                  been affected.
                </li>
                <li>
                  promptly take whatever action is necessary, at each Party's own expense, to minimise the impact
                  of such event and prevent such event from recurring.
                </li>
              </ul>
            </section>

            {/* 7. Conclusion */}
            <section className="space-y-2 text-sm sm:text-base text-foreground leading-relaxed">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">7. Conclusion</h3>
              <p className="popi-clause">
                <span className="clause-num">7.1</span>
                <span className="clause-body">
                  The client hereby consents to the Company sharing the personal information as provided for
                  herein cross border. Should the client's personal information be shared cross border, the personal
                  information will not be subject to less protection than it enjoys in terms of South Africa's data
                  privacy laws.
                </span>
              </p>
            </section>

            <div className="border-t border-border" />

            {/* Client signature acceptance */}
            <section className="space-y-4">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">
                Accepted by client by virtue of signature
              </h3>
              <p className="text-sm sm:text-base">
                Client name: <InlinePill testid="popi-clientname">{fullName}</InlinePill>
              </p>

              {/* Signature block — same style as the SARS POA page */}
              <div className="form-section signature-section">
                <p className="signature-instruction">Please draw your signature in the box below:</p>
                <div className="signature-container">
                  <SignatureCanvas
                    ref={signatureRef}
                    onEnd={handleEndStroke}
                    canvasProps={{
                      className: 'signature-canvas',
                      'data-testid': 'signature-canvas',
                    }}
                  />
                </div>
                <button
                  data-testid="button-clear-signature"
                  type="button"
                  onClick={clearSignature}
                  className="clear-signature-btn no-print"
                >
                  Clear Signature
                </button>
              </div>

              {/* Client signature label + line (matches POA style) */}
              <div className="pt-2 flex items-end gap-3 flex-wrap">
                <span className="text-sm sm:text-base font-semibold text-foreground">Client signature:</span>
                <span className="inline-block w-[253px] border-b border-gray-400" />
              </div>

              {/* Signed at / location */}
              <div className="pt-2 space-y-2">
                <p className="text-sm sm:text-base text-foreground flex flex-wrap items-center gap-2">
                  <span>Signed at</span>
                  <input
                    type="text"
                    value={signedAt}
                    onChange={handleLocationChange}
                    onBlur={(e) => setSignedAt(e.target.value.trim())}
                    placeholder="Enter location"
                    className={`bg-blue-50 px-2 py-1 w-44 focus:outline-none font-bold text-tca-blue rounded border-b-2 ${
                      locationError ? 'border-destructive' : 'border-tca-blue'
                    }`}
                    data-testid="popi-location-input"
                  />
                  <span>on this</span>
                  <span className="font-bold">{dayOfMonth}</span>
                  <span>day of</span>
                  <span className="font-bold">{monthName}</span>
                  <span className="font-bold">{fullYear}</span>
                </p>
                {locationError && (
                  <p className="text-sm text-destructive" data-testid="popi-location-error">{locationError}</p>
                )}
              </div>
            </section>

            <div className="border-t border-border" />

            {/* Company acceptance section. The stamp + director-signature images are
                rendered into the DOM but kept hidden in the browser. They become visible
                only during PDF generation (when `body.generating-pdf` is applied), so the
                final PDF embeds them automatically. */}
            <section className="space-y-4">
              <h3 className="text-lg font-bold text-tca-blue underline decoration-1 underline-offset-2">
                Accepted by Company by virtue of Director signature + Company Stamp
              </h3>
              <p className="text-sm sm:text-base">
                Company name: <strong>TC Auditors (Pty) Ltd</strong>
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                <div className="space-y-2">
                  <p className="text-sm sm:text-base font-semibold text-foreground">Company stamp:</p>
                  <img
                    src={COMPANY_STAMP_URL}
                    alt="TC Auditors Company Stamp"
                    className="pdf-only h-24 w-auto object-contain"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm sm:text-base font-semibold text-foreground">Director signature:</p>
                  <img
                    src={DIRECTOR_SIGNATURE_URL}
                    alt="Director Signature"
                    className="pdf-only h-24 w-auto object-contain"
                  />
                </div>
              </div>

              <p className="text-sm sm:text-base text-foreground flex flex-wrap items-center gap-2 pt-2">
                <span>Signed at</span>
                <span className="bg-blue-50 px-2 py-1 w-44 inline-block text-left font-bold text-tca-blue rounded border-b-2 border-tca-blue">
                  {(data.signedAtPOA && data.signedAtPOA.trim()) || ''}
                </span>
                <span>on this</span>
                <span className="font-bold">{dayOfMonth}</span>
                <span>day of</span>
                <span className="font-bold">{monthName}</span>
                <span className="font-bold">{fullYear}</span>
              </p>
            </section>

            {/* Action button */}
            <div className="pt-4 flex justify-center">
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid()}
                data-testid="btn-next-page"
                className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go to the Next Page &rarr;
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bottom back button */}
        <div className="w-full max-w-3xl flex justify-start mt-4 mb-6">
          <Button
            onClick={() => navigate(getStepPath(packageSlug, 'poa'))}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-poa-bottom"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SARS Power of Attorney
          </Button>
        </div>
      </main>

      <div className="relative">
        <PackageStepIndicator pkg={pkg} currentStepKey="popi" />
        <img src={TCA_SHORT_LOGO} alt="TCA Logo" className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80" />
      </div>

      {/* Generic error modal */}
      <Dialog open={errorModal.open} onOpenChange={(open) => setErrorModal({ ...errorModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Required Information Missing
            </DialogTitle>
            <DialogDescription className="text-base pt-2">{errorModal.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorModal({ open: false, message: '' })} className="bg-accent hover:bg-accent/90 text-accent-foreground">
              OK, I'll fix it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location-discrepancy warning modal */}
      <Dialog open={showLocationWarning} onOpenChange={setShowLocationWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-yellow-600 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Location Discrepancy
            </DialogTitle>
            <DialogDescription className="text-base pt-2 space-y-3">
              <span className="block">
                The location you entered on this page does not match the location on the previous page.
                Please ensure consistency across all pages.
              </span>
              <span className="block bg-muted/50 rounded p-3 text-sm">
                <span className="block">SARS Power of Attorney: <strong>{data.signedAtPOA || 'Not entered'}</strong></span>
                <span className="block">POPI Consent Agreement: <strong>{signedAt}</strong></span>
              </span>
              <span className="block">
                Please go back and correct the location on either the <strong>SARS Power of Attorney</strong> or
                the <strong>POPI Consent Agreement</strong> to be consistent.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowLocationWarning(false)} className="bg-accent hover:bg-accent/90 text-accent-foreground">
              OK, I'll correct it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POPIConsent;
